import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ConversationLayout as ConversationLayoutOrganism } from '@/components/organisms';
import {
  ConnectModal,
  StatusModals,
  LoadingState,
  EmptyState,
} from './components';
import { useConversationData } from './hooks/useConversationData';
import {
  useSendMessage,
  useSendTemplateMessage,
  useConnectChannel,
  useDisconnectChannel,
  uploadFile,
} from '@/api';
import {
  OMNICHANNEL_STATUS_QUERY_KEY,
  OMNICHANNEL_CONVERSATIONS_QUERY_KEY,
  postOmnichannelTestAi,
} from '@/api/queries/omnichannel';
import { useOmnichannelSocket } from './hooks/useOmnichannelSocket';
import { useIncomingMessageNotice } from './hooks/useIncomingMessageNotice';
import {
  broadcastOmnichannelConnectResult,
  subscribeOmnichannelConnectResult,
  openOmnichannelConnectBlankTab,
} from '@/lib/omnichannel/connect-channel';
import { useIsLgScreen } from '@/hooks/use-mobile';
import {
  mapClassificationToLeadStatus,
  type LeadStatus,
} from '@/lib/omnichannel/classification';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
} from '@/components/atoms';
import { buildOutboundMediaPayloads } from '@/lib/helpers/omnichannel-media';
import { usePlanGate } from '@/components/organisms';
import { renderTemplateWithVariables } from '@/lib/whatsapp-templates/preview-body';
import { applyCustomerNameToTemplateVariables } from '@/lib/whatsapp-templates/template-variables';
import type {
  ChannelType,
  ChatOutboundPayload,
  Message,
  MessageReaction,
  MessageParticipant,
  SendMessagePayload,
} from '@/types';
import { toast } from 'react-hot-toast';
import { useWhatsAppCoexistenceConnect } from '@/hooks/use-whatsapp-coexistence-connect';

const CHANNEL_TYPES = ['whatsapp', 'facebook', 'instagram'] as const;
const CONVERSATION_TABS = ['all', ...CHANNEL_TYPES] as const;

function parseChannelParam(value: string | null): ChannelType | 'all' | null {
  if (!value) return null;
  return CONVERSATION_TABS.includes(value as (typeof CONVERSATION_TABS)[number])
    ? (value as ChannelType | 'all')
    : null;
}

export const ConversationsPage = () => {
  const queryClient = useQueryClient();
  const { allowsFeature } = usePlanGate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [connectModalDefaultChannel, setConnectModalDefaultChannel] =
    useState<ChannelType>('instagram');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [showSyncingModal, setShowSyncingModal] = useState(false);
  const [selectedConversation, setSelectedConversation] = useState(
    () => searchParams.get('conversationId')?.trim() ?? ''
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<ChannelType | 'all'>(() => {
    return parseChannelParam(searchParams.get('channel')) ?? 'all';
  });
  const [selectedPageId, setSelectedPageId] = useState<string | undefined>(
    undefined
  );
  const [channelToDisconnect, setChannelToDisconnect] =
    useState<ChannelType | null>(null);
  const [composerPrefill, setComposerPrefill] = useState<{
    text: string;
    key: number;
  } | null>(null);
  const [isRespondWithAiLoading, setIsRespondWithAiLoading] = useState(false);
  const [isAiDraftPending, setIsAiDraftPending] = useState(false);
  const [leadStatusByConversationId, setLeadStatusByConversationId] = useState<
    Record<string, LeadStatus>
  >({});
  const [reactionsByMessageId, setReactionsByMessageId] = useState<
    Record<string, MessageReaction[]>
  >({});
  const [optimisticMessages, setOptimisticMessages] = useState<
    Array<
      Message & {
        conversationId: string;
        blobUrl?: string;
      }
    >
  >([]);
  const retryPayloadByMessageIdRef = useRef<
    Record<string, ChatOutboundPayload>
  >({});
  const handledOAuthCallbackRef = useRef(false);
  const selectedConversationRef = useRef(selectedConversation);
  selectedConversationRef.current = selectedConversation;

  const currentChannel: ChannelType =
    activeTab === 'all' ? 'whatsapp' : (activeTab as ChannelType);

  const openConnectModal = (channel?: ChannelType) => {
    const nextChannel =
      channel ??
      (activeTab === 'all' ? 'instagram' : (activeTab as ChannelType));
    setConnectModalDefaultChannel(nextChannel);
    setOpen(true);
  };

  const {
    conversations,
    messages,
    isLoadingAccounts,
    isCurrentChannelConnected,
    hasFacebookAccount,
    hasInstagramAccount,
    hasWhatsappAccount,
    accounts,
    conversationCounts,
    conversationsRawData,
    resolvedPageIdForMessages,
    messagesQuery,
    refreshLatestMessages,
    isRefreshingLatestMessages,
    isLoadingConversations,
  } = useConversationData(
    currentChannel,
    selectedConversation,
    activeTab,
    selectedPageId
  );

  const hasAnyAccountConnected =
    hasFacebookAccount || hasInstagramAccount || hasWhatsappAccount;

  const selectedConversationPreview = conversations.find(
    (c) => c.id === selectedConversation
  );

  const {
    showIncomingMessageNotice,
    signalIncomingMessage,
    dismissIncomingMessageNotice,
  } = useIncomingMessageNotice({
    selectedConversationId: selectedConversation,
    messages,
    listLastMessageAt: selectedConversationPreview?.lastMessageAt,
  });

  useOmnichannelSocket(hasAnyAccountConnected, {
    selectedConversationIdRef: selectedConversationRef,
    onAiReplyDraft: (reply) => {
      setComposerPrefill({ text: reply, key: Date.now() });
      setIsAiDraftPending(false);
    },
    onAiClassification: (classification, conversationId) => {
      const status = mapClassificationToLeadStatus(classification);
      if (!status) return;
      setLeadStatusByConversationId((prev) =>
        prev[conversationId] === status
          ? prev
          : { ...prev, [conversationId]: status }
      );
    },
    onIncomingThreadMessage: signalIncomingMessage,
  });

  const handleRefreshIncomingMessages = () => {
    void refreshLatestMessages()
      .then(() => dismissIncomingMessageNotice())
      .catch(() => {
        toast.error('Could not refresh messages. Please try again.');
      });
  };

  const isLgScreen = useIsLgScreen();
  const { mutate: sendMessage } = useSendMessage();
  const { mutate: sendTemplateMessage, isPending: isSendingTemplate } =
    useSendTemplateMessage();

  const { mutate: disconnectChannel, isPending: isDisconnecting } =
    useDisconnectChannel(() => {
      setChannelToDisconnect(null);
      window.location.reload();
    });

  const { mutate: connectChannel, isPending: isConnecting } = useConnectChannel(
    () => {
      setShowSuccessModal(true);
      setOpen(false);
    },
    () => {
      setShowFailureModal(true);
      setOpen(false);
    },
    () => {
      setOpen(false);
      toast.success('Continue connecting in the new tab.');
    },
    () => {
      toast.error(
        'Could not open a new tab. Allow popups for this site and try again.'
      );
    }
  );

  useEffect(() => {
    return subscribeOmnichannelConnectResult((payload) => {
      if (handledOAuthCallbackRef.current) {
        handledOAuthCallbackRef.current = false;
        return;
      }

      if (payload.type === 'success') {
        void queryClient.invalidateQueries({
          queryKey: [OMNICHANNEL_STATUS_QUERY_KEY],
        });
        void queryClient.invalidateQueries({
          queryKey: [OMNICHANNEL_CONVERSATIONS_QUERY_KEY],
        });
        setShowSuccessModal(true);
        setOpen(false);
      } else {
        setShowFailureModal(true);
        setOpen(false);
      }
    });
  }, [queryClient]);

  useEffect(() => {
    const channel = searchParams.get('channel');
    const connected = searchParams.get('connected');
    const error = searchParams.get('error');
    const conversationId = searchParams.get('conversationId')?.trim();

    if (channel && connected === 'true') {
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_STATUS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [OMNICHANNEL_CONVERSATIONS_QUERY_KEY],
      });
      handledOAuthCallbackRef.current = true;
      broadcastOmnichannelConnectResult({ type: 'success', channel });
      setSearchParams({});
      if (!conversationId) {
        toast.success(
          `${
            channel.charAt(0).toUpperCase() + channel.slice(1)
          } connected. You can close this tab and return to conversations.`
        );
      } else {
        setShowSuccessModal(true);
      }
    } else if (error || (channel && connected === 'false')) {
      handledOAuthCallbackRef.current = true;
      broadcastOmnichannelConnectResult({
        type: 'error',
        channel: channel ?? undefined,
      });
      setShowFailureModal(true);
      setSearchParams({});
    } else {
      const parsedChannel = parseChannelParam(channel);
      if (parsedChannel) {
        setActiveTab(parsedChannel);
      }
      if (conversationId) {
        setSelectedConversation(conversationId);
      }
    }
  }, [searchParams, setSearchParams, queryClient]);

  useEffect(() => {
    if (isLoadingConversations) return;

    if (conversations.length > 0) {
      const isCurrentInList = conversations.some(
        (c: { id: string }) => c.id === selectedConversation
      );
      const pendingDeepLink = searchParams.get('conversationId')?.trim();
      // On desktop (lg) always have a selection; on mobile allow empty so back shows list
      const shouldAutoSelect =
        isLgScreen && !selectedConversation && !pendingDeepLink;
      const selectionInvalid = selectedConversation && !isCurrentInList;
      if (shouldAutoSelect || selectionInvalid) {
        setSelectedConversation(conversations[0].id);
      }
    } else if (!searchParams.get('conversationId')?.trim()) {
      setSelectedConversation('');
    }
  }, [
    conversations,
    selectedConversation,
    isLgScreen,
    searchParams,
    isLoadingConversations,
  ]);

  const handleConnectConfirm = (channel: ChannelType) => {
    if (channel === 'whatsapp') {
      connectExistingWhatsapp();
      return;
    }

    const popup = openOmnichannelConnectBlankTab();
    if (!popup) {
      toast.error(
        'Could not open a new tab. Allow popups for this site and try again.'
      );
      return;
    }

    connectChannel({ channel, popup });
  };

  const {
    start: connectExistingWhatsapp,
    isPending: isConnectingExistingWhatsapp,
  } = useWhatsAppCoexistenceConnect({
    onSuccess: () => {
      setOpen(false);
      setShowSyncingModal(true);
    },
    onError: (message) => {
      toast.error(message);
      setShowFailureModal(true);
      setOpen(false);
    },
  });

  const handleSuccess = () => {};

  const isConnectingAnyChannel = isConnecting || isConnectingExistingWhatsapp;

  const resolveBaseSendContext = () => {
    const pageIdToUse = resolvedPageIdForMessages ?? selectedPageId;
    if (!selectedConversation || !pageIdToUse) return null;

    const conversationsArray = conversationsRawData ?? [];
    const selectedConv = conversationsArray.find(
      (conv) => conv?.id === selectedConversation
    );
    if (!selectedConv) return null;

    let recipientId: string | undefined;
    if ('participant' in selectedConv && selectedConv.participant) {
      recipientId = selectedConv.participant.id;
    } else if ('senders' in selectedConv) {
      const senders = selectedConv.senders?.data || [];
      const customer =
        senders.find((s: MessageParticipant) => s.id !== selectedPageId) ||
        senders[0];
      recipientId = customer?.id;
    }

    if (!recipientId) return null;

    const channel =
      'channel' in selectedConv && selectedConv.channel
        ? (selectedConv.channel as ChannelType)
        : currentChannel;

    return { pageIdToUse, recipientId, channel, selectedConv };
  };

  const resolveSendContext = () => {
    const ctx = resolveBaseSendContext();
    if (!ctx) return null;

    if (
      'responseWindow' in ctx.selectedConv &&
      ctx.selectedConv.responseWindow
    ) {
      const rw = ctx.selectedConv.responseWindow as {
        isExpired?: boolean;
        canSendFreeForm?: boolean;
      };
      if (rw.isExpired || rw.canSendFreeForm === false) {
        toast.error(
          'You can’t send free-form messages right now. Use an approved template instead.'
        );
        return null;
      }
    }

    return ctx;
  };

  const markOptimisticSent = (tempId: string, blobUrl?: string) => {
    setOptimisticMessages((prev) =>
      prev.map((m) =>
        m.id === tempId
          ? { ...m, deliveryStatus: 'sent', uploadProgress: 100 }
          : m
      )
    );
    window.setTimeout(() => {
      setOptimisticMessages((prev) => {
        const row = prev.find((m) => m.id === tempId);
        if (row?.blobUrl) URL.revokeObjectURL(row.blobUrl);
        return prev.filter((m) => m.id !== tempId);
      });
      delete retryPayloadByMessageIdRef.current[tempId];
    }, 1500);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  };

  const markOptimisticFailed = (tempId: string, message: string) => {
    setOptimisticMessages((prev) =>
      prev.map((m) =>
        m.id === tempId
          ? { ...m, deliveryStatus: 'failed', uploadProgress: undefined }
          : m
      )
    );
    toast.error(message);
  };

  const sendPayloadChain = (
    payloads: SendMessagePayload[],
    tempId: string,
    blobUrl?: string,
    index = 0
  ) => {
    if (index >= payloads.length) {
      markOptimisticSent(tempId, blobUrl);
      return;
    }

    sendMessage(payloads[index], {
      onSuccess: () => sendPayloadChain(payloads, tempId, blobUrl, index + 1),
      onError: () =>
        markOptimisticFailed(
          tempId,
          'Message failed to send. Tap retry to try again.'
        ),
    });
  };

  const addOptimisticRow = (
    tempId: string,
    row: Omit<Message, 'id'> & { blobUrl?: string }
  ) => {
    setOptimisticMessages((prev) => [
      ...prev,
      {
        ...row,
        id: tempId,
        conversationId: selectedConversation,
      },
    ]);
  };

  const handleOutbound = (outbound: ChatOutboundPayload) => {
    if (outbound.kind === 'reaction') {
      setReactionsByMessageId((prev) => ({
        ...prev,
        [outbound.targetMessageId]: [
          ...(prev[outbound.targetMessageId] ?? []),
          { emoji: outbound.emoji },
        ],
      }));
      return;
    }

    const ctx = resolveSendContext();
    if (!ctx) return;

    const createdAt = new Date().toISOString();
    const time = new Date().toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });

    if (outbound.kind === 'text') {
      const tempId = `tmp-${Date.now()}`;
      retryPayloadByMessageIdRef.current[tempId] = outbound;

      addOptimisticRow(tempId, {
        sender: 'You',
        message: outbound.text,
        time,
        isOutgoing: true,
        createdAt,
        deliveryStatus: 'pending',
        mediaType: 'text',
      });

      sendPayloadChain(
        [
          {
            conversationId: selectedConversation,
            channel: ctx.channel,
            messageType: 'text',
            message: outbound.text,
            to: ctx.recipientId,
            pageId: ctx.pageIdToUse,
          },
        ],
        tempId
      );
      return;
    }

    if (outbound.kind !== 'media') return;

    const sendOneMedia = (file: File, caption?: string) => {
      const tempId = `tmp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const blobUrl = URL.createObjectURL(file);
      const retryPayload: ChatOutboundPayload = {
        kind: 'media',
        mediaType: outbound.mediaType,
        files: [file],
        ...(caption?.trim() ? { caption: caption.trim() } : {}),
      };
      retryPayloadByMessageIdRef.current[tempId] = retryPayload;

      const label =
        outbound.mediaType === 'image'
          ? (caption ?? '')
          : outbound.mediaType === 'voice'
            ? 'Voice message'
            : caption?.trim() || '[Document]';

      addOptimisticRow(tempId, {
        sender: 'You',
        message: label,
        time,
        isOutgoing: true,
        createdAt,
        deliveryStatus: 'pending',
        mediaType: outbound.mediaType,
        mediaUrl: blobUrl,
        fileName: file.name,
        mimeType: file.type,
        fileSize: file.size,
        uploadProgress: 0,
        blobUrl,
      });

      const formData = new FormData();
      formData.append('file', file);

      void uploadFile(
        formData,
        (percent) => {
          setOptimisticMessages((prev) =>
            prev.map((m) =>
              m.id === tempId ? { ...m, uploadProgress: percent } : m
            )
          );
        },
        'omnichannel'
      )
        .then((res) => {
          const uploaded = res.data?.[0];
          if (!uploaded?.url) throw new Error('Upload failed');

          const payloads = buildOutboundMediaPayloads({
            conversationId: selectedConversation,
            channel: ctx.channel,
            to: ctx.recipientId,
            pageId: ctx.pageIdToUse,
            uploadUrl: uploaded.url,
            uiMediaType: outbound.mediaType,
            caption,
          });

          sendPayloadChain(payloads, tempId, blobUrl);
        })
        .catch((e) => {
          setOptimisticMessages((prev) =>
            prev.map((m) =>
              m.id === tempId
                ? { ...m, deliveryStatus: 'failed', uploadProgress: undefined }
                : m
            )
          );
          toast.error(
            e instanceof Error
              ? e.message
              : 'Upload failed. Tap retry to try again.'
          );
        });
    };

    const files = outbound.files ?? [];
    if (!files.length) return;

    // Images: allow multi-select; caption is applied to the first image only.
    files.forEach((file, idx) => {
      const caption = idx === 0 ? outbound.caption : undefined;
      sendOneMedia(file, caption);
    });
  };

  const handleSendTemplate = (payload: {
    templateId: number;
    templateTitle: string;
    previewBody: string;
    templateVariables: Record<string, string>;
    variableNames: string[];
  }) => {
    if (!selectedConversation) return;

    const ctx = resolveBaseSendContext();
    if (!ctx) return;

    if (ctx.channel !== 'whatsapp') {
      toast.error('Templates are only available for WhatsApp conversations.');
      return;
    }

    const customerName =
      conversations.find((c) => c.id === selectedConversation)?.name?.trim() ??
      '';
    const templateVariables = applyCustomerNameToTemplateVariables(
      payload.variableNames,
      payload.templateVariables,
      customerName
    );

    const preview = renderTemplateWithVariables(
      payload.previewBody,
      templateVariables
    );
    const tempId = `tmp-template-${Date.now()}`;
    const createdAt = new Date().toISOString();
    const time = new Date().toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });

    addOptimisticRow(tempId, {
      sender: 'You',
      message: preview || payload.templateTitle,
      time,
      isOutgoing: true,
      createdAt,
      deliveryStatus: 'pending',
      mediaType: 'text',
      apiMessageType: 'template',
    });

    sendTemplateMessage(
      {
        conversationId: selectedConversation,
        pageId: ctx.pageIdToUse,
        channel: ctx.channel,
        to: ctx.recipientId,
        templateId: payload.templateId,
        templateVariables,
      },
      {
        onSuccess: () => markOptimisticSent(tempId),
        onError: () =>
          markOptimisticFailed(
            tempId,
            'Template failed to send. Please try again.'
          ),
      }
    );
  };

  const handleRetryMessage = (messageId: string) => {
    const payload = retryPayloadByMessageIdRef.current[messageId];
    if (!payload) return;
    setOptimisticMessages((prev) => {
      const row = prev.find((m) => m.id === messageId);
      if (row?.blobUrl) URL.revokeObjectURL(row.blobUrl);
      return prev.filter((m) => m.id !== messageId);
    });
    delete retryPayloadByMessageIdRef.current[messageId];
    handleOutbound(payload);
  };

  const handleReactToMessage = (messageId: string, emoji: string) => {
    handleOutbound({ kind: 'reaction', emoji, targetMessageId: messageId });
  };

  const lastInboundMessage = [...messages].reverse().find((m) => !m.isOutgoing);
  const canRespondWithAi =
    !!selectedConversation && !!lastInboundMessage && messages.length > 0;

  const uiMessages = [
    ...messages.map((m) => ({
      ...m,
      reactions: reactionsByMessageId[m.id] ?? m.reactions,
    })),
    ...optimisticMessages
      .filter((m) => m.conversationId === selectedConversation)
      .map((m) => ({
        id: m.id,
        sender: m.sender,
        message: m.message,
        time: m.time,
        isOutgoing: m.isOutgoing,
        createdAt: m.createdAt,
        deliveryStatus: m.deliveryStatus,
        mediaType: m.mediaType,
        mediaUrl: m.mediaUrl,
        fileName: m.fileName,
        mimeType: m.mimeType,
        fileSize: m.fileSize,
        uploadProgress: m.uploadProgress,
        reactions: m.reactions,
      })),
  ].sort((a, b) => {
    const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return ta - tb;
  });

  const handleRespondWithAi = async () => {
    if (!allowsFeature('aiConversations')) return;

    const pageIdToUse = resolvedPageIdForMessages ?? selectedPageId;
    if (!selectedConversation || !pageIdToUse) return;

    const conversationsArray = conversationsRawData ?? [];
    const selectedConv = conversationsArray.find(
      (conv) => conv?.id === selectedConversation
    );
    if (!selectedConv) return;

    if (!lastInboundMessage) {
      toast.error('No customer message to reply to yet.');
      return;
    }

    const numericConvId = Number.parseInt(selectedConversation, 10);
    const conversationId = Number.isNaN(numericConvId)
      ? selectedConversation
      : numericConvId;

    setIsRespondWithAiLoading(true);
    try {
      await postOmnichannelTestAi({
        message: lastInboundMessage.message,
        conversationId,
      });
      setIsAiDraftPending(true);
      toast.success(
        'AI is drafting a reply — it will appear in the composer when ready.'
      );
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : 'Could not reach the AI service.'
      );
      setIsAiDraftPending(false);
    } finally {
      setIsRespondWithAiLoading(false);
    }
  };

  const handleTabChange = (tab: string) => {
    const nextTab = parseChannelParam(tab) ?? 'all';
    setActiveTab(nextTab);
    setSelectedPageId(undefined);
    setSelectedConversation('');
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('channel', nextTab);
        next.delete('conversationId');
        return next;
      },
      { replace: true }
    );
  };

  const handlePageSelect = (pageId: string) => {
    setSelectedPageId(pageId);
    setSelectedConversation('');
  };

  useEffect(() => {
    if (accounts && accounts.length > 0 && !selectedPageId) {
      const defaultPageId =
        currentChannel === 'instagram'
          ? accounts.find((acc) => acc.instagram_business_account)
              ?.instagram_business_account?.id || accounts[0]?.pageId
          : accounts[0]?.pageId;
      if (defaultPageId) {
        setSelectedPageId(defaultPageId);
      }
    }
  }, [accounts, currentChannel, selectedPageId]);

  if (isLoadingAccounts) {
    return <LoadingState />;
  }

  if (!hasAnyAccountConnected) {
    return (
      <>
        <EmptyState onConnect={() => openConnectModal()} />
        <ConnectModal
          open={open}
          setOpen={setOpen}
          defaultChannel={connectModalDefaultChannel}
          onConnect={(channel) => handleConnectConfirm(channel)}
          isConnecting={isConnectingAnyChannel}
        />
        <StatusModals
          showSuccessModal={showSuccessModal}
          setShowSuccessModal={setShowSuccessModal}
          showFailureModal={showFailureModal}
          setShowFailureModal={setShowFailureModal}
          showSyncingModal={showSyncingModal}
          setShowSyncingModal={setShowSyncingModal}
          onSuccess={handleSuccess}
        />
      </>
    );
  }

  return (
    <>
      <ConversationLayoutOrganism
        conversations={conversations.map((c) => ({
          ...c,
          status: leadStatusByConversationId[c.id] ?? c.status,
        }))}
        selectedConversation={selectedConversation}
        searchQuery={searchQuery}
        activeTab={activeTab}
        messages={uiMessages}
        onConversationSelect={setSelectedConversation}
        onSearchChange={setSearchQuery}
        onTabChange={handleTabChange}
        onConnectChannel={() => openConnectModal()}
        onDisconnectChannel={(channel) => setChannelToDisconnect(channel)}
        isDisconnectingChannel={isDisconnecting}
        channelConnected={{
          facebook: hasFacebookAccount,
          instagram: hasInstagramAccount,
          whatsapp: hasWhatsappAccount,
        }}
        onSendMessage={handleOutbound}
        onRetryMessage={handleRetryMessage}
        onReactToMessage={handleReactToMessage}
        composerDisabled={!selectedConversation}
        isChannelConnected={isCurrentChannelConnected}
        currentChannel={currentChannel}
        onConnectChannelDirect={openConnectModal}
        onBackFromChat={() => setSelectedConversation('')}
        conversationCounts={conversationCounts}
        accounts={accounts}
        selectedPageId={selectedPageId}
        onPageSelect={handlePageSelect}
        isLoadingAccounts={isLoadingAccounts}
        composerPrefill={composerPrefill}
        onRespondWithAi={handleRespondWithAi}
        isRespondWithAiLoading={isRespondWithAiLoading}
        isAiDraftPending={isAiDraftPending}
        canRespondWithAi={canRespondWithAi}
        showIncomingMessageNotice={showIncomingMessageNotice}
        isRefreshingIncomingMessages={
          isRefreshingLatestMessages || !!messagesQuery?.isFetchingPreviousPage
        }
        onRefreshIncomingMessages={handleRefreshIncomingMessages}
        onDismissIncomingMessageNotice={dismissIncomingMessageNotice}
        showTemplates={currentChannel === 'whatsapp'}
        onSendTemplate={handleSendTemplate}
        isSendingTemplate={isSendingTemplate}
      />

      <ConnectModal
        open={open}
        setOpen={setOpen}
        defaultChannel={connectModalDefaultChannel}
        onConnect={(channel) => handleConnectConfirm(channel)}
        isConnecting={isConnectingAnyChannel}
      />

      <StatusModals
        showSuccessModal={showSuccessModal}
        setShowSuccessModal={setShowSuccessModal}
        showFailureModal={showFailureModal}
        setShowFailureModal={setShowFailureModal}
        showSyncingModal={showSyncingModal}
        setShowSyncingModal={setShowSyncingModal}
        onSuccess={handleSuccess}
      />

      {/* Disconnect confirmation modal */}
      <Dialog
        open={channelToDisconnect !== null}
        onOpenChange={(open) => !open && setChannelToDisconnect(null)}
      >
        <DialogContent showCloseButton={true}>
          <DialogHeader>
            <DialogTitle>Disconnect channel?</DialogTitle>
            <DialogDescription>
              Are you sure you want to disconnect{' '}
              {channelToDisconnect
                ? channelToDisconnect.charAt(0).toUpperCase() +
                  channelToDisconnect.slice(1)
                : ''}
              ? You can reconnect at any time.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setChannelToDisconnect(null)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isDisconnecting}
              onClick={() => {
                if (channelToDisconnect) {
                  disconnectChannel(channelToDisconnect);
                }
              }}
            >
              {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
